import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { JSONText } from '$tablet-app/src/app/developer/components/JSONText';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import { Text } from '@akin-home/text/native';
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from '@akin-home/toast/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import {
  useCreateRobotIdentity,
  useDeleteRobotIdentity,
  useUpdateRobotIdentity,
} from '../hooks/mutations';
import { useEditRobotFaceEmbeddings } from '../hooks/useEditRobotFaceEmbeddings';
import { RobotIdentity } from '../types';
import { RobotIdentityFormScreenView } from './RobotIdentityFormScreenView';

const context = {
  canSave: true,
  fields: {
    name: { type: 'text' },
    notes: { type: 'text' },
  },
};

const parameters = {
  defaults: {
    name: 'text',
    email: 'text',
    phone: {
      number: 'text',
      type: 'text',
    },
  },
};

export const CreateRobotIdentityScreen = (
  props: RootStackScreenProps<'CreateRobotIdentity'>
) => {
  const {
    navigation: { goBack },
    route: {
      params: { robotId, defaults = {} },
    },
  } = props;

  const robot = useRobotStateStore((state) => state.robotDevices[robotId]);

  if (!robot) {
    showErrorToast({
      text1: 'Robot not found.',
      text2: 'Please try again later.',
    });
    goBack();
  }

  const [identity, setIdentity] = useState<Partial<RobotIdentity>>(defaults);

  useUpdateScreenChatContext(context);

  const createMutation = useCreateRobotIdentity(robot);
  const updateMutation = useUpdateRobotIdentity(robot);
  const deleteMutation = useDeleteRobotIdentity(robot);

  const onSubmit = useSaveCommand(
    useCallback(async () => {
      if (createMutation.isLoading || !identity) return;

      if (!identity.name) {
        showErrorToast({
          text1: 'Name of Identity cannot be blank.',
        });
        return;
      }

      showInfoToast({ text1: 'Creating Identity...' });

      try {
        // if we have already created the identity, then we update instead of creating
        if (identity.uuid) {
          await updateMutation.mutateAsync({
            identity_id: identity.uuid,
            identityUpdates: identity,
          });
        } else {
          await createMutation.mutateAsync(identity);
        }

        // We have succesfully created the identity, so it is no longer temporary.
        tempIDCreated.current = false;
        showSuccessToast({
          text1: 'New Identity Created',
        });
        goBack();
      } catch (err) {
        console.error('Error when submitting create identity:', err);
        showErrorToast({
          text1: 'Error in Creating New Identity',
          text2: 'Please try again.',
        });
      }
    }, [createMutation, goBack, identity, updateMutation])
  );

  const {
    isLoading: isFaceEmbeddingsLoading,
    onCreateFaceEmbeddings,
    onChangeFaceEmbeddings,
    onDeleteFaceEmbeddings,
  } = useEditRobotFaceEmbeddings(robot, {
    createOptions: {
      onSuccess: (newIdent) =>
        setIdentity({
          ...identity,
          uuid: newIdent?.uuid,
          has_face_embeddings: true,
        }),
    },
    updateOptions: {
      onSuccess: () => setIdentity({ ...identity, has_face_embeddings: true }),
    },
    deleteOptions: {
      onSuccess: () => setIdentity({ ...identity, has_face_embeddings: false }),
    },
  });

  const handleChangeFaceEmbeddings = async () => {
    if (!identity.uuid) {
      if (!identity.name) {
        showErrorToast({
          text1: 'Error in Learning Face',
          text2: 'Please specify a name first',
        });
        return;
      }

      const newUUID = await onCreateFaceEmbeddings(identity.name);
      if (newUUID) {
        tempIDCreated.current = true;
      }
    } else {
      await onChangeFaceEmbeddings(identity.uuid);
    }
  };

  const handleDeleteFaceEmbeddings = async () => {
    if (!identity.uuid) {
      return;
    }
    return await onDeleteFaceEmbeddings(identity.uuid);
  };

  /**
   * Tracks whether we have created a temporary ID on the robot that has not actually
   * been created on the app yet. For example, if we create face embeddings before pressing save.
   * This is then used to delete the ID, if the user cancels the create robot action.
   */
  const tempIDCreated = useRef<boolean>(false);

  /**
   * Remove the temporary ID created when leaving the screen
   */
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (tempIDCreated.current && identity.uuid) {
        deleteMutation
          .mutateAsync(identity.uuid)
          .catch((err) =>
            console.error('Error when deleting temporary identity:', err)
          );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity.uuid]);

  return identity ? (
    <>
      <RobotIdentityFormScreenView
        screenType={'create'}
        identity={identity}
        onChange={setIdentity}
        onCancel={goBack}
        isSubmitting={createMutation.isLoading}
        onSubmit={onSubmit}
        isEditingFaceEmbeddings={isFaceEmbeddingsLoading}
        onChangeFaceEmbeddings={handleChangeFaceEmbeddings}
        onDeleteFaceEmbeddings={handleDeleteFaceEmbeddings}
      />

      <DeveloperScreenInfo
        context={<JSONText>{context}</JSONText>}
        parameters={<Text>{JSON.stringify(parameters, null, 3)}</Text>}
      />
    </>
  ) : null;
};
